/**
 * MTB Mantine theme — GENERATED FROM THE DESIGN SYSTEM. Do not hand-edit.
 * Source: design-tokens/tokens.json -> mytcgbinder-ds/platforms/mantine/
 *
 * Targets Mantine 9.2.x (the version in both mytcgbinder-vendor and
 * mytcgbinder-storefront). Both apps import this identical file; publish it as
 * a shared workspace package (e.g. @mtb/theme) so the two stay in lockstep.
 *
 * Pair it with mantine-bridge.css, which points Mantine's own CSS variables at
 * the MTB semantic tokens. Import order in your app entry:
 *
 *   import '@mantine/core/styles.css';
 *   import '@mtb/ds/styles.css';            // MTB tokens
 *   import '@mtb/theme/mantine-bridge.css'; // Mantine vars -> MTB tokens
 *
 * and set the density + scheme attributes on <html>:
 *   <html data-density="compact" data-scheme="light" {...mantineHtmlProps}>
 */
// This file intentionally has NO @mantine/core import so it can be validated
// and type-checked outside a Mantine install. In your app:
//
//   import { createTheme, MantineProvider } from '@mantine/core';
//   import { mtbThemeOverride } from '@mtb/theme';
//   const theme = createTheme(mtbThemeOverride);
//
// A 10-shade Mantine color tuple.
type MantineColorsTuple = [
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
];

/** MTB Mint — the brand ramp, matching apps/web/src/theme.ts in both the
 *  vendor and storefront repos byte for byte. Shade 6 (#0FA882) is the
 *  light-mode primary and shade 5 (#2BC28E) the dark-mode primary, exactly
 *  as production ships them. Filled controls carry NEAR-BLACK labels: white
 *  on shade 6 is 3.02:1 and fails AA, near-black is 6.62:1. autoContrast
 *  below makes Mantine pick that automatically. */
const brand: MantineColorsTuple = [
  '#EFFAF6',
  '#D6F1E6',
  '#A9E5CF',
  '#76D8B5',
  '#4DCCA0',
  '#2BC28E',
  '#0FA882',
  '#0A8869',
  '#086C55',
  '#065543',
];

/** Violet — secondary; "wanted" markers, non-destructive alternate actions. */
const violet: MantineColorsTuple = [
  '#F5F3FF',
  '#EDE9FE',
  '#DDD6FE',
  '#C4B5FD',
  '#A78BFA',
  '#8B5CF6',
  '#7C3AED',
  '#6D28D9',
  '#5B21B6',
  '#4C1D95',
];

/** Cyan — tertiary; info, the card-ink gradient start. */
const cyan: MantineColorsTuple = [
  '#ECFEFF',
  '#CFFAFE',
  '#A5F3FC',
  '#67E8F9',
  '#22D3EE',
  '#06B6D4',
  '#0891B2',
  '#0E7490',
  '#155E75',
  '#164E63',
];

/** Ink — the MTB neutral spine. Replaces Mantine's default gray/dark so
 *  surfaces match the consumer brand's near-blacks exactly. */
const ink: MantineColorsTuple = [
  '#F5F5F7',
  '#EBEBEF',
  '#DCDCE2',
  '#B9B9C2',
  '#8E8E99',
  '#6B6B75',
  '#4A4A52',
  '#33333A',
  '#1C1C24',
  '#0E0E12',
];

/** Mantine's `dark` tuple drives dark-scheme body/surface/border colors.
 *  Index 7 = body background, 6 = surface, 4 = borders. */
const dark: MantineColorsTuple = [
  '#F5F5F7',
  '#B9B9C2',
  '#8E8E99',
  '#6B6B75',
  '#33333A',
  '#1C1C24',
  '#16161C',
  '#0E0E12',
  '#0B0B0E',
  '#08080A',
];

const red: MantineColorsTuple = [
  '#FEF2F2',
  '#FEE2E2',
  '#FECACA',
  '#FCA5A5',
  '#F87171',
  '#EF4444',
  '#DC2626',
  '#B91C1C',
  '#991B1B',
  '#7F1D1D',
];

const amber: MantineColorsTuple = [
  '#FFFBEB',
  '#FEF3C7',
  '#FDE68A',
  '#FCD34D',
  '#FBBF24',
  '#F59E0B',
  '#D97706',
  '#B45309',
  '#92400E',
  '#78350F',
];

export const mtbThemeOverride = {
  primaryColor: 'brand',
  primaryShade: { light: 6, dark: 5 },
  /** Filled variants compute their own label color against the fill, which is
   *  what keeps mint-600 buttons on near-black text instead of failing white.
   *
   *  luminanceThreshold is 0.25, NOT Mantine's default 0.3: mint-600
   *  (#0FA882) has a relative luminance of 0.2972 — a hair UNDER 0.3 — so the
   *  default flips it to white text at 3.02:1, which fails AA. At 0.25 it
   *  resolves to theme.black (#08080A) at 6.62:1. Do not raise this without
   *  re-measuring the primary fills. */
  autoContrast: true,
  luminanceThreshold: 0.25,
  colors: { brand, violet, cyan, ink, dark, red, amber },

  white: '#FFFFFF',
  black: '#08080A',

  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  fontFamilyMonospace: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  headings: {
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
    fontWeight: '500',
    sizes: {
      h1: { fontSize: '30px', lineHeight: '1.2', fontWeight: '400' },
      h2: { fontSize: '24px', lineHeight: '1.25' },
      h3: { fontSize: '20px', lineHeight: '1.3' },
      h4: { fontSize: '16px', lineHeight: '1.4' },
      h5: { fontSize: '14px', lineHeight: '1.45' },
      h6: { fontSize: '12px', lineHeight: '1.5' },
    },
  },

  /** Matches the MTB 4px grid (tokens/spacing.css). */
  spacing: { xs: '4px', sm: '8px', md: '16px', lg: '24px', xl: '32px' },
  fontSizes: {
    xs: '11px',
    sm: '12px',
    md: '14px',
    lg: '16px',
    xl: '18px',
  },
  lineHeights: {
    xs: '1.3',
    sm: '1.4',
    md: '1.5',
    lg: '1.55',
    xl: '1.65',
  },
  radius: { xs: '2px', sm: '4px', md: '8px', lg: '12px', xl: '16px' },
  /** 'md' (8px) matches both apps' existing defaultRadius — production made
   *  this call deliberately and there is no reason to churn every control. */
  defaultRadius: 'md',

  shadows: {
    xs: '0 1px 2px rgba(0,0,0,0.10)',
    sm: '0 2px 6px rgba(0,0,0,0.12)',
    md: '0 4px 12px rgba(0,0,0,0.16)',
    lg: '0 8px 24px rgba(0,0,0,0.20)',
    xl: '0 16px 48px rgba(0,0,0,0.26)',
  },

  /** Vendor + storefront are desk-density: 32px default controls. */
  cursorType: 'pointer',
  focusRing: 'auto',

  components: {
    Button: { defaultProps: { size: 'sm', fw: 500 } },
    ActionIcon: { defaultProps: { size: 'md', variant: 'subtle' } },
    TextInput: { defaultProps: { size: 'sm' } },
    NumberInput: { defaultProps: { size: 'sm' } },
    Select: { defaultProps: { size: 'sm' } },
    MultiSelect: { defaultProps: { size: 'sm' } },
    Autocomplete: { defaultProps: { size: 'sm' } },
    Textarea: { defaultProps: { size: 'sm' } },
    DatePickerInput: { defaultProps: { size: 'sm' } },
    Badge: { defaultProps: { variant: 'light', tt: 'uppercase', fw: 500 } },
    Table: {
      defaultProps: { verticalSpacing: 'xs', horizontalSpacing: 'sm', highlightOnHover: true },
    },
    Card: { defaultProps: { radius: 'md', padding: 'md', withBorder: true } },
    Paper: { defaultProps: { radius: 'md', withBorder: true } },
    Modal: { defaultProps: { radius: 'md', centered: true, overlayProps: { blur: 2 } } },
    Drawer: { defaultProps: { radius: 0, position: 'right', size: 'lg' } },
    Menu: { defaultProps: { shadow: 'md', withinPortal: true } },
    Tooltip: { defaultProps: { withArrow: true, openDelay: 300 } },
    Tabs: { defaultProps: {} },
    SegmentedControl: { defaultProps: { size: 'sm' } },
    Notification: { defaultProps: { withBorder: true } },
    Anchor: { defaultProps: { underline: 'hover' } },
    Code: { defaultProps: {} },
  },

  other: {
    /** Non-Mantine MTB values components can read via theme.other.* */
    density: 'compact',
    tapMin: 44,
    gradientInk: 'linear-gradient(135deg, #22D3EE 0%, #A78BFA 50%, #F0ABFC 100%)',
    forTrade: '#2BC28E',
    wanted: '#A78BFA',
    binder: '#15120E',
    eyebrow: {
      fontFamily: '"JetBrains Mono", ui-monospace, monospace',
      fontSize: '11px',
      letterSpacing: '0.18em',
      textTransform: 'uppercase',
    },
    motion: { fast: 150, standard: 250, slow: 400 },
    easing: {
      standard: 'cubic-bezier(0.4, 0, 0.2, 1)',
      decelerate: 'cubic-bezier(0, 0, 0.2, 1)',
      accelerate: 'cubic-bezier(0.4, 0, 1, 1)',
    },
  },
} as const;

export default mtbThemeOverride;
